import { Element } from '../Element';

/**
 * ShapeElement represents a shape on the timeline
 * Aligned with Creatomate properties and structure
 */
export class ShapeElement extends Element {
  private _path: string | null = null;

  constructor(props: Partial<ShapeElement> = {}) {
    super({ ...props, type: 'shape' });

    // Set shape-specific properties
    this._path = props.path ?? null;

    // // Shape elements can have null width and height to use unboxed coordinates
    // if (props.width === null) this.update({ width: null });
    // if (props.height === null) this.update({ height: null });
  }

  // Getters and setters
  get path(): string | null {
    return this._path;
  }

  set path(value: string | null) {
    this._path = value;
  }

  /**
   * Creates a deep clone of this shape element
   * Override the base Element.clone() to ensure we create a ShapeElement, not a generic Element
   */
  override clone(): ShapeElement {
    const el = new ShapeElement({
      ...this.getState(),
      // Ensure specific shape properties are copied
      path: this._path,
      // metadata removed from base Element class
    });

    return el;
  }

  protected override getAdditionalProperties(): Record<string, any> {
    // Include all shape-specific properties in the JSON
    const props: Record<string, any> = {
      // Always include path property, even if null
      path: this._path,
    };

    return props;
  }

  /**
   * Creates a ShapeElement from JSON data
   */
  static override fromJSON(json: Record<string, any>): ShapeElement {
    // Parse time-based properties that may be formatted as strings
    const parsedTime = Element.parseTimeProperty(
      json.time ?? json.start ?? json.globalTime ?? 0,
    );
    const parsedDuration = Element.parseTimeProperty(json.duration);

    // Ensure parsedTime is a number, default to 0 if null
    const timeValue = parsedTime !== null ? Number(parsedTime) : 0;

    return new ShapeElement({
      // Base element properties
      id: json.id,
      track: json.track ?? null,
      time: timeValue,
      duration: parsedDuration,
      type: 'shape', // Force shape type
      dynamic: json.dynamic ?? false,
      locked: json.locked ?? false,
      visible: json.visible ?? true,
      x: json.x ?? '50%',
      y: json.y ?? '50%',
      z_index: json.z_index ?? null,
      width: json.width ?? '100%', // Can be null for unboxed coordinates
      height: json.height ?? '100%', // Can be null for unboxed coordinates
      fill_color: json.fill_color ?? null,
      // metadata removed from base Element class

      // Shape-specific properties
      path: json.path ?? null,
    });
  }

  /**
   * Captures the mutable state of the shape element.
   * Extends the base Element.getState() to include shape-specific properties.
   * @returns A record of all mutable properties.
   */
  override getState(): Record<string, any> {
    // Get the base element state first
    const baseState = super.getState();

    // Add shape-specific properties
    return {
      ...baseState,
      path: this._path,
    };
  }

  /**
   * Restores the shape element's mutable state from a state record.
   * Extends the base Element.restoreState() to handle shape-specific properties.
   * @param state The state to restore.
   */
  override restoreState(state: Record<string, any>): void {
    // First, restore base element properties
    super.restoreState(state);

    // Then restore shape-specific properties
    if (state.path !== undefined) this._path = state.path;
  }
}
